import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@stoneage/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;

    title?: TypographyProps;
    brandAttribute?: InjectableCss;
    attributeRow?: InjectableCss;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            margin-top: 32px;
        `,
    },
    attributeTypeLabelText: {
        css: css`
            min-width: 186px;
            max-width: 168px;
            font-weight: 400;
            margin-right: 22px;
            ${xScreen('lte', 'xs')} {
                max-width: 140px;
                min-width: 140px;
            }
        `,
    },
    attributeTypeValuesText: {
        weight: 'bold',
    },
    title: {
        size: '20px',
        lineHeight: '22px',
        weight: 'bold',
        color: 'common.black',
        css: css`
            margin-bottom: 12px;
        `,
    },
    brandAttribute: {
        css: css`
            display: flex;
            align-items: center;
            padding: 8px 12px;
            background: ${getColor('common.ui4')};
            border-bottom: 1px solid ${getColor('common.ui5')};
        `,
    },
    attributeRow: {
        css: css`
            display: flex;
            align-items: center;
            padding: 8px 12px;
            border-bottom: 1px solid ${getColor('common.ui5')};
            &:nth-child(2n) {
                background: ${({ hasBrand }: any) => (hasBrand ? getColor('common.white') : getColor('common.ui4'))};
            }
            &:nth-child(2n+1) {
                background: ${({ hasBrand }: any) => (hasBrand ? getColor('common.ui4') : getColor('common.white'))};
            }
        `,
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = ({ product: { brand, attributeTypes } }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    const limitedAttributeTypes = (attributeTypes?.filter(attributeType => attributeType.includeOnProduct) ?? []);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes">
            <Typography as="h3" {...styles.title}>{translate('Product Specifications')}</Typography>
            {brand && (
                <StyledWrapper data-test-selector="brand_item" {...styles.brandAttribute}>
                    <Typography data-test-selector="brand_item_label" {...styles.attributeTypeLabelText}>
                        {translate("Brand")}
                    </Typography>
                    <Typography translate="no" data-test-selector="brand_item_value" {...styles.attributeTypeValuesText}>{brand.name}</Typography>
                </StyledWrapper>
            )}
            {limitedAttributeTypes.map(attributeType => (
                <StyledWrapper
                    data-attributetypeid={attributeType.id}
                    key={attributeType.id.toString()}
                    data-test-selector="attributes_item"
                    {...styles.attributeRow}
                    {...({ hasBrand: brand && brand.name.length > 0 } as any)}
                >
                    <Typography data-test-selector="attributes_item_label" {...styles.attributeTypeLabelText}>
                        {attributeType.label || attributeType.name}
                    </Typography>
                    <Typography translate="no" data-test-selector="attributes_item_value" {...styles.attributeTypeValuesText}>
                        {(attributeType.attributeValues ?? [])
                            .map(attributeValue => attributeValue.valueDisplay)
                            .join(", ")}
                    </Typography>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
